import React from 'react';
import { isNil } from 'lodash';
import Button from '../../../../../components/Button';
import {
  addAllItemToSelectList,
  reDirectPage,
  setColumnWidth,
  setDash,
  setDetailPageUrl,
  setNumberWithComma,
} from '../../../../../lib/common';
import Select from '../../../../../components/input/Select/Select';
import CouponBenefit from '../../../../coupon/common/CouponBenefit';
import CouponBenefitInfo from '../../../../coupon/common/CouponBenefitInfo';
import { setDateFormat } from '../../../../../lib/moment';
import OpenCouponDetail from '../../../../../components/link/OpenCouponDetail';
import CouponPeriod from '../../../../coupon/common/CouponPeriod';
import Anchor from '../../../../../components/link/Anchor';
import OpenOrderItemDetail from '../../../../../components/link/OpenOrderItemDetail';
import { setCouponGenerateLimitCount } from '../../../../../lib/utils/couponUtils';
import { DATE_FORMAT } from '../../../../../constants/service/common/utilConstants';
import {
  COUPON_DETAIL_URL_PATH,
  COUPON_ISSUE_HISTORY_DETAIL_URL_PATH,
} from '../../../../../constants/service/common/urlConstants';
import {
  COUPON_AVAILABLE_TYPE_SELECT_LIST,
  COUPON_AVAILABLE_TYPE_TEXT,
  COUPON_DISCOUNT_TYPE_SELECT_LIST,
  COUPON_IS_USED_TYPE_SELECT_LIST,
  COUPON_IS_USED_TYPE_TEXT,
  COUPON_STATUS_TYPE_TEXT,
} from '../../../../../constants/service/app/couponConstants';
import TableFilterInput from '../../../../../components/table/TableFilterInput';
import DateRangePicker from '../../../../../components/datePicker/DateRangePicker';

/**
 * 쿠폰 리스트 컬럼
 */
export const columns = (data) => {
  const { list, filterOption, onFilter } = data;

  return [
    {
      Header: '상세보기',
      fixed: 'left',
      width: 100,
      Cell: (row) => (
        <Button
          isRadius
          color="primary"
          text="상세보기"
          onClick={() =>
            reDirectPage({
              path: setDetailPageUrl(COUPON_DETAIL_URL_PATH, row.original.id),
            })
          }
        />
      ),
    },
    {
      accessor: 'isUsed',
      Header: '사용여부',
      width: 100,
      fixed: 'left',
      filterable: true,
      Cell: (row) => COUPON_IS_USED_TYPE_TEXT[row.value] || '_',
      Filter: () => (
        <Select
          list={COUPON_IS_USED_TYPE_SELECT_LIST}
          onChange={(isUsed) => onFilter({ isUsed })}
          value={filterOption.isUsed}
          isTable={true}
          size="large"
        />
      ),
    },
    {
      accessor: 'couponId',
      Header: '쿠폰 ID',
      fixed: 'left',
      filterable: true,
      Cell: (row) => <OpenCouponDetail text={row.value} couponId={row.value} />,
      Filter: () => <TableFilterInput option={filterOption.couponId} onFilter={(couponId) => onFilter({ couponId })} />,
    },
    {
      accessor: 'couponGeneratedId',
      Header: '쿠폰 발급 ID',
      fixed: 'left',
      filterable: true,
      Filter: () => (
        <TableFilterInput
          option={filterOption.couponGeneratedId}
          onFilter={(couponGeneratedId) => onFilter({ couponGeneratedId })}
        />
      ),
    },
    {
      accessor: 'name',
      Header: '쿠폰명',
      className: 'align-left',
      filterable: true,
      resizable: true,
      fixed: 'left',
      width: setColumnWidth(list, 'name'),
      Filter: () => <TableFilterInput option={filterOption.name} onFilter={(name) => onFilter({ name })} />,
    },
    {
      accessor: 'description',
      Header: '쿠폰 설명',
      className: 'align-left',
      filterable: true,
      resizable: true,
      fixed: 'left',
      width: 200,
      Cell: (row) => setDash(row.value),
      Filter: () => (
        <TableFilterInput option={filterOption.description} onFilter={(description) => onFilter({ description })} />
      ),
    },
    {
      accessor: 'discountType',
      Header: '쿠폰 혜택',
      fixed: 'left',
      width: 120,
      filterable: true,
      Cell: (row) => <CouponBenefit type={row.value} amount={row.original.discountAmount} />,
      Filter: () => (
        <Select
          list={addAllItemToSelectList(COUPON_DISCOUNT_TYPE_SELECT_LIST)}
          onChange={(discountType) => onFilter({ discountType })}
          defaultValue={filterOption.discountType}
          value={filterOption.discountType}
          isTable={true}
          size="large"
        />
      ),
    },
    {
      accessor: 'status',
      Header: '쿠폰 상태',
      width: 100,
      Cell: (row) => COUPON_STATUS_TYPE_TEXT[row.value] || '_',
    },
    {
      accessor: 'couponIssueHistoryId',
      Header: '발급 내역',
      width: setColumnWidth(list, 'couponIssueHistoryId'),
      Cell: (row) =>
        !isNil(row.value) ? (
          <Anchor
            text="자세히 보기"
            color="primary"
            href={setDetailPageUrl(COUPON_ISSUE_HISTORY_DETAIL_URL_PATH, row.value)}
            target="_blank"
          />
        ) : (
          '-'
        ),
    },
    {
      accessor: 'availableType',
      Header: '쿠폰 유형',
      width: 140,
      filterable: true,
      Cell: (row) => COUPON_AVAILABLE_TYPE_TEXT[row.value] || '_',
      Filter: () => (
        <Select
          list={addAllItemToSelectList(COUPON_AVAILABLE_TYPE_SELECT_LIST)}
          onChange={(availableType) => onFilter({ availableType })}
          defaultValue={filterOption.availableType}
          value={filterOption.availableType}
          isTable={true}
          size="large"
        />
      ),
    },
    {
      accessor: 'code',
      Header: '입력 코드',
      width: 100,
      filterable: true,
      Cell: (row) => setDash(row.value),
      Filter: () => <TableFilterInput option={filterOption.code} onFilter={(code) => onFilter({ code })} />,
    },
    {
      accessor: 'generateLimitCount',
      Header: '제한 횟수',
      width: 100,
      Cell: (row) => setCouponGenerateLimitCount(row.original.generateLimitDay, row.original.generateLimitCount),
    },
    {
      Header: '쿠폰 혜택 정보',
      resizable: true,
      width: 160,
      Cell: (row) => (
        <CouponBenefitInfo
          type={row.original.discountType}
          amount={row.original.discountAmount}
          minimumPurchasePrice={row.original.minimumPurchasePrice}
          maxDiscountPrice={row.original.maxDiscountPrice}
        />
      ),
    },
    {
      accessor: 'createDate',
      Header: '발급일자',
      filterable: true,
      width: 320,
      Cell: (row) => setDateFormat(row.value, DATE_FORMAT.YMD_DASH_FORMAT),
      Filter: () => (
        <DateRangePicker
          value={filterOption.generateDate}
          outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
          onSelect={(generateDate) => onFilter({ generateDate })}
        />
      ),
    },
    {
      accessor: 'endDate',
      Header: '만료일자',
      filterable: true,
      width: 320,
      Cell: (row) => setDateFormat(row.value, DATE_FORMAT.YMD_DASH_FORMAT),
      Filter: () => (
        <DateRangePicker
          value={filterOption.endDate}
          outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
          onSelect={(endDate) => onFilter({ endDate })}
        />
      ),
    },
    {
      Header: '사용기간',
      width: 290,
      Cell: (row) => (
        <CouponPeriod
          type={row.original.periodType}
          startDate={row.original.startDate}
          endDate={row.original.endDate}
          dayPlus={row.original.dayPlus}
        />
      ),
    },
    {
      accessor: 'memo',
      Header: '메모',
      resizable: true,
      width: setColumnWidth(list, 'memo'),
    },
    {
      accessor: 'useDate',
      Header: '사용일자',
      filterable: true,
      width: 160,
      Cell: (row) => setDateFormat(row.value, DATE_FORMAT.YMD_DASH_FORMAT),
      Filter: () => (
        <DateRangePicker
          value={filterOption.useDate}
          outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
          onSelect={(useDate) => onFilter({ useDate })}
        />
      ),
    },
    {
      accessor: 'orderItemId',
      Header: '상품 주문번호',
      filterable: true,
      width: 150,
      Cell: (row) => <OpenOrderItemDetail text={row.value} orderItemId={row.value} />,
      Filter: () => (
        <TableFilterInput option={filterOption.orderItemId} onFilter={(orderItemId) => onFilter({ orderItemId })} />
      ),
    },
    {
      accessor: 'orderPrice',
      Header: '총 주문금액',
      width: 160,
      Cell: (row) => setNumberWithComma(row.value),
    },
    {
      accessor: 'quantity',
      Header: '수량',
      width: 80,
      Cell: (row) => setNumberWithComma(row.value),
    },
    {
      accessor: 'usedPoint',
      Header: '포인트 할인 금액',
      width: 160,
      Cell: (row) => setNumberWithComma(row.value),
    },
    {
      accessor: 'usedCouponPrice',
      Header: '쿠폰 할인 금액',
      width: 160,
      Cell: (row) => setNumberWithComma(row.value),
    },
    {
      accessor: 'totalPrice',
      Header: '실제 결제 금액',
      width: 160,
      Cell: (row) => setNumberWithComma(row.value),
    },
  ];
};
